<template>
  <div class="container">
    <h2>{{ $t("register.welcome") }}</h2>
    <p>
      {{ $t("register.help") }}
    </p>
    <div class="register-form" v-if="submitted">
      <p>{{ $t("register.success") }}</p>
    </div>
    <form
      class="register-form"
      novalidate
      @submit.prevent="registerUser"
      v-if="!submitted"
    >
      <div class="row">
        <div class="col-sm">
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.companyName.$invalid }"
          >
            <label>{{ $t("register.companyName") }}</label>
            <input
              type="text"
              name="company"
              class="form-control"
              :class="{ 'is-invalid': $v.companyName.$error }"
              v-model="$v.companyName.$model"
            />
            <div class="invalid-feedback" v-if="!$v.companyName.required">
              Field is required
            </div>
          </div>
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.addressLine1.$invalid }"
          >
            <label>{{ $t("register.addressLine1") }}</label>
            <input
              type="text"
              name="line1"
              class="form-control"
              :class="{ 'is-invalid': $v.addressLine1.$error }"
              v-model.trim="$v.addressLine1.$model"
            />
            <div class="invalid-feedback" v-if="!$v.addressLine1.required">
              Field is required
            </div>
          </div>
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.addressLine2.$invalid }"
          >
            <label>{{ $t("register.addressLine2") }}</label>
            <input
              type="text"
              name="line2"
              class="form-control"
              v-model.trim="$v.addressLine2.$model"
            />
            <div class="invalid-feedback" v-if="!$v.addressLine2.required">
              Field is required
            </div>
          </div>
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.addressLine3.$invalid }"
          >
            <label>{{ $t("register.addressLine3") }}</label>
            <input
              type="text"
              name="line3"
              class="form-control"
              :class="{ 'is-invalid': $v.addressLine3.$error }"
              v-model.trim="$v.addressLine3.$model"
            />
            <div class="invalid-feedback" v-if="!$v.addressLine3.required">
              Field is required
            </div>
          </div>
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.city.$invalid }"
          >
            <label>{{ $t("register.city") }}</label>
            <input
              type="text"
              name="city"
              class="form-control"
              :class="{ 'is-invalid': $v.city.$error }"
              v-model.trim="$v.city.$model"
            />
            <div class="invalid-feedback" v-if="!$v.city.required">
              Field is required
            </div>
          </div>
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.zip.$invalid }"
          >
            <label>{{ $t("register.zip") }}</label>
            <input
              type="text"
              name="zip"
              class="form-control"
              :class="{ 'is-invalid': $v.zip.$error }"
              v-model.trim="$v.zip.$model"
            />
            <div class="invalid-feedback" v-if="!$v.zip.required">
              Field is required
            </div>
          </div>
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.country.$invalid }"
          >
            <label>{{ $t("register.country") }}</label>
            <select
              class="custom-select"
              name="country"
              v-model.trim="$v.country.$model"
            >
              <option
                v-for="country in getCountries()"
                v-bind:key="country.code"
                v-bind:value="country.code"
                >{{ country.name }}</option
              >
            </select>
            <div class="invalid-feedback" v-if="!$v.country.required">
              Field is required
            </div>
          </div>
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.state.$invalid }"
          >
            <label>{{ $t("register.state") }}</label>
            <input
              type="text"
              name="state"
              class="form-control"
              :class="{ 'is-invalid': $v.state.$error }"
              v-model.trim="$v.state.$model"
            />
            <div class="invalid-feedback" v-if="!$v.state.required">
              Field is required
            </div>
          </div>
        </div>
        <div class="col-sm">
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.email.$invalid }"
          >
            <label>{{ $t("register.email") }}</label>
            <input
              type="email"
              name="email"
              class="form-control"
              :class="{ 'is-invalid': $v.email.$error }"
              v-model="$v.email.$model"
            />
            <div class="invalid-feedback" v-if="!$v.email.required">
              Field is required
            </div>
            <div class="invalid-feedback" v-if="!$v.email.invalid">
              Must be a valid email address.
            </div>
          </div>
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.givenName.$invalid }"
          >
            <label>{{ $t("register.givenName") }}</label>
            <input
              type="text"
              name="given_name"
              class="form-control"
              :class="{ 'is-invalid': $v.givenName.$error }"
              v-model.trim="$v.givenName.$model"
            />
            <div class="invalid-feedback" v-if="!$v.givenName.required">
              Field is required
            </div>
            <div
              class="invalid-feedback"
              v-if="!$v.givenName.minLength || !$v.givenName.maxLength"
            >
              Name must be between
              {{ $v.givenName.$params.minLength.min }} &
              {{ $v.givenName.$params.maxLength.max }} characters.
            </div>
          </div>
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.surName.$invalid }"
          >
            <label>{{ $t("register.surName") }}</label>
            <input
              type="text"
              name="surname"
              class="form-control"
              :class="{ 'is-invalid': $v.surName.$error }"
              v-model.trim="$v.surName.$model"
            />
            <div class="invalid-feedback" v-if="!$v.surName.required">
              Field is required
            </div>
            <div
              class="invalid-feedback"
              v-if="!$v.surName.minLength || !$v.surName.maxLength"
            >
              Name must be between
              {{ $v.surName.$params.minLength.min }} &
              {{ $v.surName.$params.maxLength.max }} characters.
            </div>
          </div>
          <div
            class="form-group"
            :class="{ 'was-validated': !$v.phone.$invalid }"
          >
            <label>{{ $t("register.phone") }}</label>
            <input
              type="text"
              name="phone"
              class="form-control"
              :class="{ 'is-invalid': $v.phone.$error }"
              v-model.trim="$v.phone.$model"
            />
            <div class="invalid-feedback" v-if="!$v.phone.required">
              Field is required
            </div>
          </div>
          <div class="form-group agreement">
            <p-check v-model="$v.agreedToTerms.$model">
              <i18n path="register.agreement" for="terms"> </i18n>
            </p-check>
            <a
              style="font-size: 1.7rem; line-height: 1.7rem;"
              :href="termsLink"
              target="_blank"
            >
              {{ $t("register.terms") }}
            </a>
          </div>
          <input
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="$v.$invalid"
            :value="$t('register.save')"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import axios from "axios";
import { validationMixin } from "vuelidate";
import {
  email,
  maxLength,
  minLength,
  required,
  sameAs,
  alpha
} from "vuelidate/lib/validators";
import { getData } from "country-list";
import { translate } from "@/i18n";
import { settings } from "@xsenscloud/xse-styleguide";
import { XsensUSACountries } from "@/utils/countries-helper";

@Component({
  mixins: [validationMixin],
  validations: {
    companyName: {
      required
    },
    email: {
      email,
      required
    },
    givenName: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(50)
    },
    surName: {
      required,
      minLength: minLength(1),
      maxLength: maxLength(50)
    },
    phone: {
      required
    },
    addressLine1: {
      required
    },
    addressLine2: {
      alpha
    },
    addressLine3: {},
    city: {
      required
    },
    zip: {
      required
    },
    country: {
      required
    },
    state: {},
    agreedToTerms: {
      sameAs: sameAs(() => true)
    }
  }
})
export default class RegisterView extends Vue {
  submitted = false;
  companyName = "";
  email = "";
  givenName = "";
  surName = "";
  phone = "";
  addressLine1 = "";
  addressLine2 = "";
  addressLine3 = "";
  city = "";
  zip = "";
  private selectedCountry = "";
  state = "";
  agreedToTerms = false;

  get country() {
    return this.selectedCountry;
  }

  set country(country) {
    const isUSA = XsensUSACountries.includes(this.selectedCountry);
    const becomesUSA = XsensUSACountries.includes(country);
    if (isUSA !== becomesUSA) {
      this.agreedToTerms = false;
    }
    this.selectedCountry = country;
  }

  getCountries() {
    return getData();
  }

  get termsLink() {
    return XsensUSACountries.includes(this.selectedCountry)
      ? "https://www.xsens.com/motioncloud-agreement-americas"
      : "https://www.xsens.com/motioncloud-agreement";
  }

  async registerUser() {
    const tenantEndpoint = settings.TENANT_BILLING_API_ENDPOINT as Record<
      string,
      string
    >;
    // Always use emea (Billing API decides tenancy)
    const endpoint = tenantEndpoint["emea"];
    return axios
      .post(`${endpoint}/user/register`, {
        companyName: this.companyName,
        email: this.email,
        givenName: this.givenName,
        surName: this.surName,
        phone: this.phone,
        addressLine1: this.addressLine1,
        addressLine2: this.addressLine2,
        addressLine3: this.addressLine3,
        city: this.city,
        zip: this.zip,
        country: this.selectedCountry,
        state: this.state
      })
      .then(() => {
        this.$snotify.success(translate("register.success").toString());
        this.submitted = true;
      })
      .catch(error => {
        this.$snotify.error(error.response.data.message);
      });
  }
}
</script>

<style lang="scss" scoped>
.register-form {
  padding: 60px 100px;
  margin-bottom: 60px;
  border: 1px solid $gray-200;
}
</style>
